import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'receptionist',
    doctorId: ''
  });
  const [validated, setValidated] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [roles, setRoles] = useState([]);
  
  const { username, email, password, confirmPassword, role, doctorId } = formData;
  const { register, user, error, setError } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      // Only admins can register new users
      if (user.role !== 'admin') {
        navigate('/');
      }
    } else {
      navigate('/login');
    }
    
    return () => {
      // Clear any errors when component unmounts
      if (setError) setError(null);
    };
  }, [user, navigate, setError]);
  
  // Load doctors and roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        const doctorsRes = await axios.get('/api/doctors.php');
        setDoctors(doctorsRes.data);
        
        // Load roles from API
        try {
          const rolesRes = await axios.get('/api/roles.php');
          setRoles(rolesRes.data);
        } catch (err) {
          // Fallback to default roles if API fails
          setRoles([
            { id: 1, name: 'admin' },
            { id: 2, name: 'doctor' },
            { id: 3, name: 'receptionist' }
          ]);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    
    fetchData();
  }, []);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // Check password match
    if (e.target.name === 'confirmPassword' || e.target.name === 'password') {
      if (e.target.name === 'confirmPassword') {
        setPasswordMatch(e.target.value === password);
      } else {
        setPasswordMatch(confirmPassword === e.target.value);
      }
    }
  };

  const onSubmit = async e => {
    e.preventDefault();
    
    // Check passwords match
    if (password !== confirmPassword) {
      setPasswordMatch(false);
      return;
    }
    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    const userData = {
      username,
      email,
      password,
      role
    };
    
    if (role === 'doctor' && doctorId) {
      userData.doctorId = doctorId;
    }
    
    const success = await register(userData);
    if (success) {
      navigate('/users');
    }
  };
  
  return (
    <Container>
      <Row className="justify-content-center mt-5">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-4">
                <Button 
                  variant="outline-primary" 
                  className="me-3" 
                  onClick={() => navigate('/users')}
                  size="sm"
                >
                  &larr; Back to Users
                </Button>
                <h2 className="m-0">Register New User</h2>
              </div>
              
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={onSubmit} autoComplete="off">
                <Row>
                  <Col>
                    <Form.Group controlId="username" className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter username"
                        name="username"
                        value={username}
                        onChange={onChange}
                        required
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a username.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group controlId="email" className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        name="email"
                        value={email}
                        onChange={onChange}
                        required
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a valid email.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group controlId="password" className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter password"
                        name="password"
                        value={password}
                        onChange={onChange}
                        required
                        autoComplete="new-password"
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide a password.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group controlId="confirmPassword" className="mb-3">
                      <Form.Label>Confirm Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Confirm password"
                        name="confirmPassword"
                        value={confirmPassword}
                        onChange={onChange}
                        isInvalid={!passwordMatch}
                        required
                        autoComplete="new-password"
                      />
                      <Form.Control.Feedback type="invalid">
                        Passwords do not match.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group controlId="role" className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={role}
                        onChange={onChange}
                        required
                      >
                        <option value="">Select Role...</option>
                        {roles.map(r => (
                          <option key={r.id} value={r.name}>{r.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    
                    {role === 'doctor' && (
                      <Form.Group controlId="doctorId" className="mb-3">
                        <Form.Label>Select Doctor</Form.Label>
                        <Form.Select
                          name="doctorId"
                          value={doctorId}
                          onChange={onChange}
                          required={role === 'doctor'}
                        >
                          <option value="">Select a doctor...</option>
                          {doctors.map(doctor => (
                            <option key={doctor.id} value={doctor.id}>
                              {doctor.name} - {doctor.specialization}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          Please select a doctor.
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                    
                    <Button variant="primary" type="submit" className="mt-3">
                      Register
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register; 